import { useState } from "react";
import useSWR from "swr";
import { useSearchParams } from "react-router-dom";
import { Box, Group, Title, Select, Button, Modal, TextInput, Stack, Text, Alert, ActionIcon, Tooltip, UnstyledButton } from "@mantine/core";
import { Trash2, Check, Pencil, Plus, Search } from "lucide-react";
import { useFetch, getTemplates, getTemplate, saveTemplate, deleteTemplate, copyTemplate, getSubjects, Template, PLATFORM_YOUTUBE, PLATFORM_VK } from "../../../shared/api";
import type { TemplateData } from "../types";
import { ACCESS_LINK } from "../utils";
import classes from "./TemplatesPanel.module.css";

function platformSummary(data: TemplateData): string {
  const names: Record<string, string> = { [PLATFORM_YOUTUBE]: "YouTube", [PLATFORM_VK]: "VK" };
  return (data.platforms ?? []).map(p => `${names[p.platform] ?? p.platform} (${p.access === ACCESS_LINK ? "по ссылке" : "публичный"})`).join(", ");
}

export function TemplatesPanel({ snapshot, currentSubjectId, onApply }: { snapshot: TemplateData; currentSubjectId: number | null; onApply: (d: TemplateData, sId: number | null) => void }) {
  const { fetch } = useFetch();
  const { data: templates, mutate } = useSWR("submit/templates", () => getTemplates({ fetch }));
  const { data: subjects } = useSWR("admin/subjects", () => getSubjects({ fetch }));

  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Template | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saveSubjectId, setSaveSubjectId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const sharedId = searchParams.get("template");
  const { data: sharedTemplate } = useSWR(sharedId ? ["submit/template", sharedId] : null, () => getTemplate({ id: sharedId as string, fetch }));

  const subjectTitle = (id: number | null) => id == null ? null : subjects?.find(s => s.id === id)?.title ?? null;
  const shown = (templates ?? []).filter(t => (t.title || "").toLowerCase().includes(filter.trim().toLowerCase()));

  async function doSave() {
    setSaving(true); setError(null);
    try {
      await saveTemplate({ template: { title: saveTitle, subject_id: saveSubjectId ? Number(saveSubjectId) : null, data: snapshot as Record<string, unknown> }, fetch });
      setSaveOpen(false); setSaveTitle(""); setSaveSubjectId(null); await mutate();
    } catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setSaving(false); }
  }

  async function runOp(t: Template, op: () => Promise<any>, msg: string) {
    if (!window.confirm(msg)) return;
    setError(null); setBusyId(t.id);
    try { await op(); await mutate(); }
    catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setBusyId(null); }
  }

  return (
    <Box>
      <Group justify="space-between" mb="sm" align="center">
        <Title order={4}>Шаблоны</Title>
        <Tooltip label="Сохранить как шаблон"><ActionIcon variant="subtle" onClick={() => { setSaveSubjectId(currentSubjectId != null ? String(currentSubjectId) : null); setSaveOpen(true); }}><Plus size={18} /></ActionIcon></Tooltip>
      </Group>
      <TextInput mb="sm" placeholder="Выберите шаблон" value={filter} onChange={e => setFilter(e.currentTarget.value)} leftSection={<Search size={16} />} />
      {error && <Alert color="red" mb="sm">{error}</Alert>}
      {sharedTemplate && (
        <Alert color="blue" mb="sm" title="Вам прислали шаблон">
          <Text mb="xs">«{sharedTemplate.title || "(без названия)"}»</Text>
          <Group gap="xs">
            <Button size="xs" onClick={() => onApply((sharedTemplate.data || {}) as TemplateData, sharedTemplate.subject_id ?? null)}>Применить</Button>
            <Button size="xs" variant="default" onClick={async () => { await copyTemplate({ id: sharedTemplate.id, fetch }); await mutate(); searchParams.delete("template"); setSearchParams(searchParams, { replace: true }); }}>Сохранить себе</Button>
          </Group>
        </Alert>
      )}
      <Stack gap={2}>
        {shown.map(t => {
          const subj = subjectTitle(t.subject_id);
          return (
            <Group key={t.id} className={classes.row} justify="space-between" wrap="nowrap" gap="xs">
              <Tooltip label={<>{t.title || "(без названия)"}{subj ? ` · ${subj}` : ""}</>} withArrow openDelay={300} multiline maw={280}>
                <UnstyledButton style={{ minWidth: 0, flex: 1, textAlign: "left" }} onClick={() => setPreview(t)}>
                  <Text fz="sm" fw={500} truncate>{t.title || "(без названия)"}</Text>
                  {subj && <Text fz="xs" c="dimmed" truncate>{subj}</Text>}
                </UnstyledButton>
              </Tooltip>
              <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                <Tooltip label="Применить"><ActionIcon variant="subtle" size="sm" onClick={() => onApply((t.data || {}) as TemplateData, t.subject_id ?? null)}><Check size={16} /></ActionIcon></Tooltip>
                <Tooltip label="Перезаписать"><ActionIcon variant="subtle" size="sm" loading={busyId === t.id} onClick={() => runOp(t, () => saveTemplate({ template: { id: t.id, subject_id: currentSubjectId, data: snapshot as Record<string, unknown> }, fetch }), `Перезаписать «${t.title}»?`)}><Pencil size={16} /></ActionIcon></Tooltip>
                <Tooltip label="Удалить"><ActionIcon variant="subtle" color="red" size="sm" loading={busyId === t.id} onClick={() => runOp(t, () => deleteTemplate({ id: t.id, fetch }), `Удалить «${t.title}»?`)}><Trash2 size={16} /></ActionIcon></Tooltip>
              </Group>
            </Group>
          );
        })}
        {shown.length === 0 && <Text c="dimmed" fz="sm" py="xs">{templates ? "Шаблонов нет" : "Загрузка…"}</Text>}
      </Stack>

      <Modal opened={saveOpen} onClose={() => setSaveOpen(false)} title="Сохранить шаблон">
        <Stack>
          <TextInput label="Название" value={saveTitle} onChange={e => setSaveTitle(e.currentTarget.value)} />
          <Select label="Предмет" placeholder="Не выбран" data={(subjects ?? []).map(s => ({ value: String(s.id), label: s.title }))} value={saveSubjectId} onChange={setSaveSubjectId} searchable clearable />
          <Group justify="flex-end"><Button variant="default" onClick={() => setSaveOpen(false)}>Отмена</Button><Button onClick={doSave} loading={saving} disabled={!saveTitle.trim()}>Сохранить</Button></Group>
        </Stack>
      </Modal>

      <Modal opened={!!preview} onClose={() => setPreview(null)} title={preview?.title || "(без названия)"} size="lg">
        {preview && (() => {
          const d = (preview.data || {}) as TemplateData;
          return (
            <Stack gap="md">
              <Box p="md" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-md)" }}>
                <Stack gap="xs">
                  {[["Предмет", subjectTitle(preview.subject_id) ?? d.subjectName], ["Платформы", platformSummary(d)], ["Лектор", d.structured?.metadata?.lecturer], ["Оператор", d.structured?.staff?.operator], ["Монтажёр", d.structured?.staff?.editor], ["Плейлист YouTube", d.structured?.references?.youtube], ["Плейлист ВК", d.structured?.references?.vk], ["Доп. сообщение", d.structured?.additionalMessage]].map(([l, v]) => (
                    <Group key={l as string} justify="space-between" wrap="nowrap" gap="md"><Text fz="sm" c="dimmed">{l}:</Text><Text fz="sm" ta="right">{v || "—"}</Text></Group>
                  ))}
                </Stack>
              </Box>
              <Group justify="flex-end"><Button variant="default" onClick={() => setPreview(null)}>Закрыть</Button><Button onClick={() => { onApply(d, preview.subject_id ?? null); setPreview(null); }}>Применить</Button></Group>
            </Stack>
          );
        })()}
      </Modal>
    </Box>
  );
}
